// ScoreComponent.tsx
import { FC } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SORT_OPTION } from '../../services/enum';
import { Prono } from '../../services/type';


interface ScoreComponentProps {
    prono: Prono;
    sortSelected: SORT_OPTION;
}

const getBgForValue = (value: number, thresholds: { high: number; medium: number }) => {
    if (value >= thresholds.high) return 'success.main';
    if (value >= thresholds.medium) return 'primary.main';
    return 'error.main';
};

// Cote : plus BAS = meilleur (favori), donc logique inversée
const getBgForCote = (value: number, thresholds: { high: number; medium: number }) => {
    if (value <= thresholds.high) return 'success.main';
    if (value <= thresholds.medium) return 'primary.main';
    return 'error.main';
};

interface ValueBlockProps {
    value: string;
    label: string;
    isSelected: boolean;
    bgColor: string;
    isMobile: boolean;
    showBorderLeft?: boolean;
    initialeTextColor?: string;
}

const ValueBlock: FC<ValueBlockProps> = ({
    value,
    label,
    isSelected,
    bgColor,
    isMobile,
    showBorderLeft = true,
    initialeTextColor
}) => {
    const boxPx = isMobile ? 1 : 1.5;
    const boxPy = isMobile ? 0.5 : 0.75;
    const minHeight = isMobile ? 34 : 40;
    const valueHeight = isMobile ? 22 : 28;
    const smallFontSize = isMobile ? 13 : 16;
    const bigFontSize = isMobile ? 18 : 24;
    const labelFontSize = isMobile ? 8 : 9;


    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={boxPx}
            py={boxPy}
            minHeight={minHeight}
            flex={1}
            bgcolor={isSelected ? bgColor : 'transparent'}
            borderLeft={showBorderLeft ? '0.5px solid' : 'none'}
            borderColor="divider"
            sx={{ transition: 'background-color 0.2s ease' }}
        >
            <Box height={valueHeight} display="flex" alignItems="center">
                <Typography
                    fontWeight={800}
                    fontSize={isSelected ? bigFontSize : smallFontSize}
                    color={isSelected ? 'white' : initialeTextColor || 'text.secondary'}
                >
                    {value}
                </Typography>
            </Box>
            <Typography
                fontSize={labelFontSize}
                color={isSelected ? 'white' : 'text.disabled'}
                sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                    opacity: isSelected ? 0.85 : 1,
                }}
            >
                {label}
            </Typography>
        </Box >
    );
};

// Seuils calibrés par valeur
const THRESHOLDS = {
    cote: { high: 4, medium: 15 },       // cote <= 3 = favori net, <= 6 = correct
    elo: { high: 1673, medium: 1484 },  // p90 / p50 mesurés en base
    probability: { high: 30, medium: 15 },
    total: { high: 7, medium: 4 },
};

export const ScoreComponent: FC<ScoreComponentProps> = ({ prono, sortSelected }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const elo = prono.performance?.elo;
    const cote = prono.data.coteDirect;
    const isTotalPurSelected = sortSelected === SORT_OPTION.SCORE_PUR;

    const cotePart = (
        <ValueBlock
            value={String(cote)}
            label="Cote"
            isSelected={sortSelected === SORT_OPTION.COTE}
            bgColor={getBgForCote(cote || 0, THRESHOLDS.cote)}
            isMobile={isMobile}
            showBorderLeft={false}
            initialeTextColor='primary.main'
        />
    );

    const eloPart = elo != null && (
        <ValueBlock
            value={String(Math.round(elo))}
            label="Elo"
            isSelected={sortSelected === SORT_OPTION.ELO}
            bgColor={getBgForValue(elo, THRESHOLDS.elo)}
            isMobile={isMobile}
        />
    );

    const probaPart = (
        <ValueBlock
            value={`${Math.round(prono.probability || 0)}%`}
            label="Proba"
            isSelected={sortSelected === SORT_OPTION.PROBABILITY}
            bgColor={getBgForValue(prono.probability || 0, THRESHOLDS.probability)}
            isMobile={isMobile}
            showBorderLeft={!isMobile}
        />
    );

    const scorePart = (
        <ValueBlock
            value={
                isTotalPurSelected
                    ? (prono.totalPur || 0).toFixed(1)
                    : (prono.total || 0).toFixed(1)
            }
            label={isTotalPurSelected ? 's. pur' : 'Score'}
            isSelected={sortSelected === SORT_OPTION.SCORE || isTotalPurSelected}
            bgColor={getBgForValue(
                isTotalPurSelected ? (prono.totalPur || 0) : (prono.total || 0),
                THRESHOLDS.total
            )}
            isMobile={isMobile}
        />
    );

    if (isMobile) {
        return (
            <Box display="flex" flexDirection="column" gap={0.5}>
                <Box
                    display="flex"
                    flexDirection="row"
                    border="0.5px solid"
                    borderColor="divider"
                    borderRadius={2}
                    overflow="hidden"
                >
                    {probaPart}
                    {scorePart}
                </Box>
                <Box
                    display="flex"
                    flexDirection="row"
                    border="0.5px solid"
                    borderColor="divider"
                    borderRadius={2}
                    overflow="hidden"
                >
                    {cotePart}
                    {eloPart}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="row"
            border="0.5px solid"
            borderColor="divider"
            borderRadius={2}
            overflow="hidden"
        >
            {cotePart}
            {eloPart}
            {probaPart}
            {scorePart}
        </Box>
    );
};